import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  ActiveModeCommands,
  Feature,
  FeatureNames,
  HeatingCurveCommands,
  HeatingCurveProperties,
  IsStringValueProperty,
  RoomCommands,
  RoomProperties,
  ZoneDemandProperties,
} from '@vi-iot/client';

import { DevicesStore, InstallationFeaturesStore, RichDevice } from '@store';
import { UIService } from '@services';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { ReferenceRoomSettingsService } from '../../refrigerant-circuit-features-panel/reference-room-settings-overlay/service/reference-room-settings.service';
import { ReferenceRoomSettingsHelper } from '../../refrigerant-circuit-features-panel/reference-room-settings-overlay/reference-room-settings.helper';

@Component({
  selector: 'app-heating-circuit-details-overlay',
  templateUrl: './heating-circuit-details-overlay.component.html',
  styleUrls: ['./heating-circuit-details-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatingCircuitDetailsOverlayComponent implements OnInit, OnDestroy {
  heatingCurve$: Observable<Feature<HeatingCurveProperties, HeatingCurveCommands>>;
  operatingModesActive$: Observable<Feature<IsStringValueProperty, ActiveModeCommands>>;
  heatingZoneDemand$: Observable<Feature<ZoneDemandProperties, never>>;
  roomDetails$: Observable<Feature<RoomProperties, RoomCommands>>;

  private _circuitId: string;
  @Input()
  set circuitId(id: string) {
    this._circuitId = id;
    this.selectFeatures();
  }
  get circuitId(): string {
    return this._circuitId;
  }
  remoteControllerDevices: RichDevice[];
  readonly featureNames = FeatureNames;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    public devicesStore: DevicesStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    private uiService: UIService,
    private referenceRoomSettingsService: ReferenceRoomSettingsService
  ) {}

  ngOnInit(): void {
    this.operatingModesActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitActiveMode(this.circuitId)
    );

    this.heatingZoneDemand$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingZoneDemand(this.circuitId)
    );
  }

  private selectFeatures(): void {
    this.heatingCurve$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingCurve(this.circuitId)
    );

    this.roomDetails$ = this.referenceRoomSettingsService.getRoomDetailsForHeatingCircuit(
      this._circuitId
    );
  }

  openReferenceRoomOverlay(feature: Feature<RoomProperties, RoomCommands>): void {
    const view =
      feature.properties.channels.value.length > 0
        ? ReferenceRoomSettingsHelper.ReferenceRoomSettingsView.ManageRoom
        : ReferenceRoomSettingsHelper.ReferenceRoomSettingsView.NoRooms;
    this.referenceRoomSettingsService.setView(view);
    this.referenceRoomSettingsService.setHcIndex(this._circuitId);

    this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.ReferenceRoomSettings);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
